/**
 * Security Utilities Module
 *
 * Provides client-side security functions including:
 * - CSV injection prevention
 * - Email masking (PII protection)
 * - Password validation
 * - Input sanitization (XSS prevention)
 * - Rate limiting
 *
 * WARNING: These are CLIENT-SIDE protections only.
 * Proper security requires SERVER-SIDE validation!
 */

// ==================== CSV INJECTION PREVENTION ====================

/**
 * Sanitizes a value for safe CSV export
 * Prevents formula injection attacks (=, +, -, @, tab, newline)
 *
 * @param value - The value to sanitize
 * @returns Sanitized value safe for CSV export
 */
export const sanitizeCSVValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) {
    return ''
  }

  const stringValue = String(value)

  // Characters that trigger formula execution in Excel/LibreOffice
  const dangerousChars = ['=', '+', '-', '@', '\t', '\r', '\n']

  // Check if value starts with dangerous character
  const startsWithDangerousChar = dangerousChars.some(char =>
    stringValue.startsWith(char)
  )

  let sanitized = stringValue

  // Prefix with single quote to prevent formula execution
  if (startsWithDangerousChar) {
    sanitized = "'" + sanitized
  }

  // Escape double quotes by doubling them (CSV standard)
  sanitized = sanitized.replace(/"/g, '""')

  // Remove any control characters
  sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, '')

  return sanitized
}

/**
 * Converts array of arrays to CSV string with sanitization
 *
 * @param headers - Array of column headers
 * @param rows - Array of row arrays
 * @returns Sanitized CSV string
 */
export const arrayToCSV = (headers: string[], rows: (string | number)[][]): string => {
  const sanitizedHeaders = headers.map(h => sanitizeCSVValue(h))
  const sanitizedRows = rows.map(row =>
    row.map(cell => sanitizeCSVValue(cell))
  )

  const csvRows = [sanitizedHeaders, ...sanitizedRows]

  // Join cells with commas, rows with newlines
  return csvRows.map(row =>
    row.map(cell => `"${cell}"`).join(',')
  ).join('\n')
}

// ==================== EMAIL MASKING (PII PROTECTION) ====================

/**
 * Masks an email address to protect customer PII
 * Examples:
 * - john.doe@example.com → j***@example.com
 * - a@test.com → a***@test.com
 *
 * @param email - Email address to mask
 * @returns Masked email address
 */
export const maskEmail = (email: string): string => {
  if (!email || !email.includes('@')) {
    return '***@***.com' // Invalid email
  }

  const [localPart, domain] = email.split('@')

  if (localPart.length <= 1) {
    return `${localPart}***@${domain}`
  }

  // Show first character + *** + @domain
  return `${localPart[0]}***@${domain}`
}

/**
 * Masks email but shows last 4 characters before @
 * Example: john.doe@example.com → ****doe@example.com
 */
export const maskEmailAdvanced = (email: string): string => {
  if (!email || !email.includes('@')) {
    return '***@***.com'
  }

  const [localPart, domain] = email.split('@')

  if (localPart.length <= 4) {
    return `***@${domain}`
  }

  const last4 = localPart.slice(-4)
  return `****${last4}@${domain}`
}

// ==================== PASSWORD VALIDATION ====================

export interface PasswordStrength {
  score: number // 0-100
  isValid: boolean
  feedback: string[]
  strength: 'very-weak' | 'weak' | 'fair' | 'good' | 'strong'
}

/**
 * Comprehensive password validation
 * Requirements:
 * - Minimum 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 * - Not a common password
 *
 * @param password - Password to validate
 * @returns PasswordStrength object with score and feedback
 */
export const validatePassword = (password: string): PasswordStrength => {
  const feedback: string[] = []
  let score = 0

  // Check length
  if (password.length < 8) {
    feedback.push('Password must be at least 8 characters')
  } else if (password.length < 12) {
    feedback.push('Recommended: 12+ characters for better security')
    score += 20
  } else if (password.length >= 12) {
    score += 25
  } else if (password.length >= 16) {
    score += 30
  }

  // Check for uppercase
  if (!/[A-Z]/.test(password)) {
    feedback.push('Add at least one uppercase letter (A-Z)')
  } else {
    score += 15
  }

  // Check for lowercase
  if (!/[a-z]/.test(password)) {
    feedback.push('Add at least one lowercase letter (a-z)')
  } else {
    score += 15
  }

  // Check for numbers
  if (!/[0-9]/.test(password)) {
    feedback.push('Add at least one number (0-9)')
  } else {
    score += 15
  }

  // Check for special characters
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    feedback.push('Add at least one special character (!@#$%^&*)')
  } else {
    score += 20
  }

  // Check against common passwords (top 100)
  const commonPasswords = [
    'password', '12345678', 'qwerty123', 'abc123', 'password123',
    'admin123', 'letmein', 'welcome', 'monkey', '1234567890',
    'password1', 'qwertyuiop', 'password12', '123456789',
    'welcome123', 'admin', 'root', 'toor', 'pass', 'test',
    'guest', 'login', 'changeme', 'secret', 'password!',
    'p@ssw0rd', 'passw0rd', 'Pa$$w0rd', 'P@ssword123'
  ]

  if (commonPasswords.some(common =>
    password.toLowerCase().includes(common)
  )) {
    feedback.push('Password is too common. Choose something unique.')
    score = Math.max(0, score - 30) // Penalty for common password
  }

  // Check for sequential characters
  if (/(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def)/.test(password.toLowerCase())) {
    feedback.push('Avoid sequential characters (123, abc, etc.)')
    score = Math.max(0, score - 10)
  }

  // Check for repeated characters
  if (/(.)\1{2,}/.test(password)) {
    feedback.push('Avoid repeated characters (aaa, 111, etc.)')
    score = Math.max(0, score - 10)
  }

  // Bonus points for variety
  const uniqueChars = new Set(password).size
  if (uniqueChars >= 10) {
    score += 5
  }

  // Determine strength level
  let strength: PasswordStrength['strength']
  if (score < 20) strength = 'very-weak'
  else if (score < 40) strength = 'weak'
  else if (score < 60) strength = 'fair'
  else if (score < 80) strength = 'good'
  else strength = 'strong'

  // Minimum requirements for validity
  const isValid =
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) &&
    !commonPasswords.some(common => password.toLowerCase().includes(common))

  return {
    score: Math.min(100, score),
    isValid,
    feedback,
    strength
  }
}

// ==================== INPUT SANITIZATION (XSS PREVENTION) ====================

/**
 * Sanitizes user input to prevent XSS attacks
 * Escapes HTML special characters
 *
 * @param input - User input string
 * @returns Sanitized string safe for display
 */
export const sanitizeInput = (input: string): string => {
  if (!input) return ''

  const htmlEscapeMap: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  }

  return input.replace(/[&<>"'/]/g, char => htmlEscapeMap[char] || char)
}

/**
 * Strips all HTML tags from input
 * More aggressive than sanitizeInput
 *
 * @param input - User input string
 * @returns String with all HTML removed
 */
export const stripHTML = (input: string): string => {
  if (!input) return ''
  return input.replace(/<[^>]*>/g, '')
}

/**
 * Validates and sanitizes email input
 *
 * @param email - Email address
 * @returns Sanitized email or empty string if invalid
 */
export const sanitizeEmail = (email: string): string => {
  if (!email) return ''

  // Remove whitespace
  const trimmed = email.trim().toLowerCase()

  // Validate email format (RFC 5322 simplified)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

  if (!emailRegex.test(trimmed)) {
    return ''
  }

  // Additional validation: no dangerous characters
  if (/[<>()[\]\\,;:]/.test(trimmed)) {
    return ''
  }

  return trimmed
}

// ==================== RATE LIMITING ====================

interface RateLimitEntry {
  attempts: number
  firstAttempt: number
  lastAttempt: number
  lockedUntil?: number
}

const rateLimitStore: { [key: string]: RateLimitEntry } = {}

export interface RateLimitResult {
  allowed: boolean
  remainingAttempts: number
  resetIn?: number // seconds until reset
  message?: string
}

/**
 * Client-side rate limiting
 * Tracks attempts and enforces limits
 *
 * @param key - Unique identifier (e.g., 'login:email@example.com')
 * @param maxAttempts - Maximum attempts allowed
 * @param windowMs - Time window in milliseconds
 * @param lockoutMs - Lockout duration in milliseconds after max attempts
 * @returns RateLimitResult indicating if request is allowed
 */
export const checkRateLimit = (
  key: string,
  maxAttempts: number = 5,
  windowMs: number = 15 * 60 * 1000, // 15 minutes
  lockoutMs: number = 15 * 60 * 1000  // 15 minutes
): RateLimitResult => {
  const now = Date.now()
  let entry = rateLimitStore[key]

  // Check if currently locked out
  if (entry?.lockedUntil && entry.lockedUntil > now) {
    const resetIn = Math.ceil((entry.lockedUntil - now) / 1000)
    return {
      allowed: false,
      remainingAttempts: 0,
      resetIn,
      message: `Too many attempts. Try again in ${resetIn} seconds.`
    }
  }

  // Reset if window has passed
  if (!entry || (now - entry.firstAttempt) > windowMs) {
    entry = {
      attempts: 0,
      firstAttempt: now,
      lastAttempt: now
    }
    rateLimitStore[key] = entry
  }

  // Increment attempts
  entry.attempts++
  entry.lastAttempt = now

  // Check if exceeded limit
  if (entry.attempts > maxAttempts) {
    entry.lockedUntil = now + lockoutMs
    const resetIn = Math.ceil(lockoutMs / 1000)
    return {
      allowed: false,
      remainingAttempts: 0,
      resetIn,
      message: `Too many attempts. Locked out for ${Math.ceil(resetIn / 60)} minutes.`
    }
  }

  return {
    allowed: true,
    remainingAttempts: maxAttempts - entry.attempts,
    message: entry.attempts >= maxAttempts - 2
      ? `Warning: ${maxAttempts - entry.attempts} attempts remaining`
      : undefined
  }
}

/**
 * Resets rate limit for a key
 * Use after successful authentication
 */
export const resetRateLimit = (key: string): void => {
  delete rateLimitStore[key]
}

// ==================== SESSION SECURITY ====================

export interface SessionData {
  data: any
  expiresAt: number
}

/**
 * Securely stores session data with expiration
 * Uses sessionStorage instead of localStorage for better security
 *
 * @param key - Storage key
 * @param data - Data to store
 * @param expirationMinutes - Expiration time in minutes (default: 60)
 */
export const setSecureSession = (
  key: string,
  data: any,
  expirationMinutes: number = 60
): void => {
  const expiresAt = Date.now() + (expirationMinutes * 60 * 1000)

  const sessionData: SessionData = {
    data,
    expiresAt
  }

  try {
    sessionStorage.setItem(key, JSON.stringify(sessionData))
  } catch (error) {
    console.error('Failed to set session:', error)
  }
}

/**
 * Retrieves session data if not expired
 * Automatically clears expired sessions
 *
 * @param key - Storage key
 * @returns Stored data or null if expired/missing
 */
export const getSecureSession = (key: string): any | null => {
  try {
    const stored = sessionStorage.getItem(key)
    if (!stored) return null

    const sessionData: SessionData = JSON.parse(stored)

    // Check if expired
    if (Date.now() > sessionData.expiresAt) {
      sessionStorage.removeItem(key)
      return null
    }

    return sessionData.data
  } catch (error) {
    console.error('Failed to get session:', error)
    return null
  }
}

/**
 * Clears session data
 */
export const clearSecureSession = (key: string): void => {
  sessionStorage.removeItem(key)
}

// ==================== LOGGING (Production-Safe) ====================

/**
 * Production-safe console logging
 * Only logs in development environment
 */
export const devLog = (...args: any[]): void => {
  if (process.env.NODE_ENV === 'development' || import.meta.env.DEV) {
    console.log('[DEV]', ...args)
  }
}

export const devError = (...args: any[]): void => {
  if (process.env.NODE_ENV === 'development' || import.meta.env.DEV) {
    console.error('[DEV ERROR]', ...args)
  }
}

export const devWarn = (...args: any[]): void => {
  if (process.env.NODE_ENV === 'development' || import.meta.env.DEV) {
    console.warn('[DEV WARN]', ...args)
  }
}

// ==================== UTILITY FUNCTIONS ====================

/**
 * Generates a secure random string
 * Use for CSRF tokens, nonces, etc.
 */
export const generateSecureToken = (length: number = 32): string => {
  const array = new Uint8Array(length)
  crypto.getRandomValues(array)
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * Constant-time string comparison to prevent timing attacks
 */
export const secureCompare = (a: string, b: string): boolean => {
  if (a.length !== b.length) return false

  let result = 0
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i)
  }
  return result === 0
}

/**
 * Validates and sanitizes URL to prevent open redirect
 */
export const sanitizeRedirectURL = (url: string, allowedDomains: string[]): string => {
  try {
    const parsed = new URL(url, window.location.origin)

    // Only allow same origin or whitelisted domains
    const isAllowed =
      parsed.origin === window.location.origin ||
      allowedDomains.some(domain => parsed.hostname.endsWith(domain))

    if (!isAllowed) {
      return '/' // Default safe redirect
    }

    return parsed.href
  } catch {
    return '/' // Invalid URL, redirect to home
  }
}

// Export all functions
export default {
  // CSV
  sanitizeCSVValue,
  arrayToCSV,

  // Email
  maskEmail,
  maskEmailAdvanced,

  // Password
  validatePassword,

  // Input
  sanitizeInput,
  stripHTML,
  sanitizeEmail,

  // Rate Limiting
  checkRateLimit,
  resetRateLimit,

  // Session
  setSecureSession,
  getSecureSession,
  clearSecureSession,

  // Logging
  devLog,
  devError,
  devWarn,

  // Utilities
  generateSecureToken,
  secureCompare,
  sanitizeRedirectURL
}
